import { getIdToken } from './firebase';
import { cacheInvalidate } from './cache';

/**
 * Sempre mesma origem: em produção a API é servida ao lado do console e, em
 * dev, o proxy do Vite encaminha `/api` (veja vite.config.ts). Manter assim
 * evita CORS e deixa o dev server livre para rodar em qualquer porta.
 */
const API_BASE = import.meta.env.VITE_API_BASE || '/api';

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = await getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data as T;
}

/**
 * Chamada que altera estado no servidor. Depois de concluída, derruba o cache
 * dos caminhos afetados para que a próxima leitura venha do servidor.
 */
export async function apiMutate<T = unknown>(
  path: string,
  options: RequestInit,
  invalidate: string[] = [],
): Promise<T> {
  const result = await apiFetch<T>(path, options);
  invalidate.forEach(cacheInvalidate);
  return result;
}

export async function apiUpload(path: string, formData: FormData) {
  const token = await getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || res.statusText);
  return data;
}

export type MeResponse = {
  firebase_uid: string;
  email: string;
  display_name: string;
  role: string;
  role_label: string;
  permissions: string[];
};

export function hasPerm(perms: string[], required: string | string[]): boolean {
  if (perms.includes('*')) return true;
  const list = Array.isArray(required) ? required : [required];
  return list.some((r) => perms.includes(r));
}
